"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExecutionId = exports.ExecutionIdSchema = exports.ExecutionIdSomeSchema = exports.ExecutionIdNoneSchema = exports.EXECUTION_ID_BOOTING = exports.EXECUTION_ID_NONE = void 0;
const zod_1 = require("zod");
/**
 * ExecutionId represents the state of a Cloud Run Job execution ID.
 *
 * This type uses an Option/Either pattern to represent three distinct states:
 * - None("none"): No execution exists (default state, or execution completed/cleared)
 * - None("booting"): Execution is being started but hasn't received an ID yet
 * - Some(value): Has an actual execution ID from Cloud Run
 *
 * The "booting" state is important to prevent the watchdog from trying to resolve
 * an old executionId during the boot phase when a new job is starting.
 *
 * Serialization format (stored in Firestore as a string):
 * - undefined or "none" -> None("none")
 * - "booting" -> None("booting")
 * - any other string -> Some(value)
 */
// Reserved string values that cannot be used as actual execution IDs
exports.EXECUTION_ID_NONE = "none";
exports.EXECUTION_ID_BOOTING = "booting";
// Type for the "None" variant - represents absence of an execution ID
exports.ExecutionIdNoneSchema = zod_1.z.object({
    _tag: zod_1.z.literal("None"),
    reason: zod_1.z.enum([exports.EXECUTION_ID_NONE, exports.EXECUTION_ID_BOOTING]),
});
// Type for the "Some" variant - represents a valid execution ID
exports.ExecutionIdSomeSchema = zod_1.z.object({
    _tag: zod_1.z.literal("Some"),
    value: zod_1.z.string(),
});
// Union type representing all possible states
exports.ExecutionIdSchema = zod_1.z.union([exports.ExecutionIdNoneSchema, exports.ExecutionIdSomeSchema]);
/**
 * Helper functions for working with ExecutionId values.
 *
 * These functions provide type-safe ways to create, serialize, deserialize,
 * and inspect ExecutionId values.
 */
exports.ExecutionId = {
    // ============ Constructors ============
    /**
     * Creates a None("none") value representing no execution.
     */
    none: () => ({ _tag: "None", reason: exports.EXECUTION_ID_NONE }),
    /**
     * Creates a None("booting") value representing an execution that is starting.
     */
    booting: () => ({ _tag: "None", reason: exports.EXECUTION_ID_BOOTING }),
    /**
     * Creates a Some(value) representing an actual execution ID.
     */
    some: (value) => ({ _tag: "Some", value }),
    // ============ Serialization ============
    /**
     * Serializes an ExecutionId to a string for Firestore storage.
     *
     * - None("none") -> undefined (to save storage space)
     * - None("booting") -> "booting"
     * - Some(value) -> value
     */
    serialize: (id) => {
        if (id._tag === "None") {
            // Return undefined for "none" to save storage, "booting" as string
            return id.reason === exports.EXECUTION_ID_NONE ? undefined : id.reason;
        }
        return id.value;
    },
    /**
     * Deserializes a string from Firestore to an ExecutionId.
     *
     * - undefined, "", or "none" -> None("none")
     * - "booting" -> None("booting")
     * - any other string -> Some(value)
     */
    deserialize: (value) => {
        if (!value || value === exports.EXECUTION_ID_NONE) {
            return exports.ExecutionId.none();
        }
        if (value === exports.EXECUTION_ID_BOOTING) {
            return exports.ExecutionId.booting();
        }
        return exports.ExecutionId.some(value);
    },
    // ============ Type Guards ============
    /**
     * Returns true if the ExecutionId is a None variant (either "none" or "booting").
     */
    isNone: (id) => id._tag === "None",
    /**
     * Returns true if the ExecutionId is a Some variant (has an actual ID).
     */
    isSome: (id) => id._tag === "Some",
    /**
     * Returns true if the ExecutionId is specifically None("booting").
     * This indicates an execution is starting but hasn't received an ID yet.
     */
    isBooting: (id) => id._tag === "None" && id.reason === exports.EXECUTION_ID_BOOTING,
    /**
     * Returns true if the ExecutionId is specifically None("none").
     * This indicates no execution exists.
     */
    isAbsent: (id) => id._tag === "None" && id.reason === exports.EXECUTION_ID_NONE,
    // ============ Value Extraction ============
    /**
     * Gets the actual execution ID value if present, undefined otherwise.
     * Use this when you need the raw string ID for API calls.
     */
    getValue: (id) => {
        if (id._tag === "Some") {
            return id.value;
        }
        return undefined;
    },
    /**
     * Gets the actual execution ID value, throwing if not present.
     * Use this when you're certain the ID exists and want to fail fast if it doesn't.
     */
    getValueOrThrow: (id) => {
        if (id._tag === "Some") {
            return id.value;
        }
        throw new Error(`Expected ExecutionId to have a value, but got None("${id.reason}")`);
    },
    // ============ Pattern Matching ============
    /**
     * Pattern match on an ExecutionId, providing exhaustive handling of all variants.
     *
     * This provides type-level exhaustiveness checking - TypeScript will error if
     * you don't handle all cases. The callbacks receive the narrowed type, so you
     * get full type safety without manual type guards.
     *
     * @example
     * const result = ExecutionId.match(executionId, {
     *     Some: (value) => `Got ID: ${value}`,
     *     None: (reason) => `No ID: ${reason}`,
     * });
     */
    match: (id, handlers) => {
        if (id._tag === "Some") {
            return handlers.Some(id.value);
        }
        return handlers.None(id.reason);
    },
    // ============ Convenience Functions ============
    /**
     * Creates an ExecutionId from a raw string value (convenience for deserialization).
     * Alias for deserialize().
     */
    fromString: (value) => exports.ExecutionId.deserialize(value),
    /**
     * Converts an ExecutionId to a string for storage (convenience for serialization).
     * Alias for serialize().
     */
    toString: (id) => exports.ExecutionId.serialize(id),
};
//# sourceMappingURL=executionId.js.map